import axios, { type AxiosInstance, type AxiosError } from 'axios';
import { jwtDecode, type JwtPayload } from 'jwt-decode';
import { storage } from './storage';

// Base API configuration
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api';

interface CustomJwtPayload extends JwtPayload {
  userId?: string;
  role?: string;
}

class ApiClient {
  private client: AxiosInstance;
  private accessToken: string | null = null;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add auth token
    this.client.interceptors.request.use(
      (config) => {
        if (this.accessToken) {
          config.headers.Authorization = `Bearer ${this.accessToken}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor for error handling
    this.client.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        if (error.response?.status === 401) {
          // Token expired, try to refresh
          const success = await this.refreshToken();
          if (success && error.config) {
            // Retry the original request
            return this.client.request(error.config);
          } else {
            // Refresh failed, redirect to login
            await this.clearTokens();
            window.location.href = '/login';
          }
        }
        return Promise.reject(error);
      }
    );

    // Initialize token from storage
    this.initializeAuth();
  }

  private async initializeAuth() {
    const token = await storage.get('accessToken');
    const loginTimestamp = await storage.get('loginTimestamp');
    const MAX_LOGIN_AGE_MS = 120 * 24 * 60 * 60 * 1000; // 120 days in ms
    //console.log('[Auth] initializeAuth token:', token);
    //console.log('[Auth] initializeAuth loginTimestamp:', loginTimestamp);
    if (token && loginTimestamp) {
      try {
        const decoded = jwtDecode(token);
        const currentTime = Date.now() / 1000;
        const loginTime = parseInt(loginTimestamp, 10);
        const now = Date.now();
        // Check both JWT expiry and 120-day window
        if (
          decoded.exp && decoded.exp > currentTime &&
          now - loginTime <= MAX_LOGIN_AGE_MS
        ) {
          this.accessToken = token;
         // console.log('[Auth] initializeAuth: token valid, accessToken set');
        } else {
          //console.log('[Auth] initializeAuth: token expired or login window exceeded');
          await this.clearTokens();
        }
      } catch (error) {
      //  console.error('[Auth] initializeAuth: Invalid token:', error);
        await this.clearTokens();
      }
    } else {
      //console.log('[Auth] initializeAuth: token or loginTimestamp missing');
      await this.clearTokens();
    }
  }

  private async refreshToken(): Promise<boolean> {
    try {
      const refreshToken = await storage.get('refreshToken');
      if (!refreshToken) return false;

      const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
        refreshToken,
      });

      const { accessToken, refreshToken: newRefreshToken } = response.data.data;
      await this.setTokens(accessToken, newRefreshToken);
      return true;
    } catch (error) {
      console.error('Token refresh failed:', error);
      return false;
    }
  }

  async setTokens(accessToken: string, refreshToken: string) {
    this.accessToken = accessToken;
    // Store tokens for 120 days (web only)
    if (typeof window !== 'undefined' && window.localStorage) {
      const expiry = Date.now() + 120 * 24 * 60 * 60 * 1000;
      window.localStorage.setItem('accessToken', accessToken);
      window.localStorage.setItem('refreshToken', refreshToken);
      window.localStorage.setItem('loginTimestamp', Date.now().toString());
      window.localStorage.setItem('tokenExpiry', expiry.toString());
    } else {
      await storage.set('accessToken', accessToken);
      await storage.set('refreshToken', refreshToken);
      await storage.set('loginTimestamp', Date.now().toString());
      await storage.set('tokenExpiry', (Date.now() + 120 * 24 * 60 * 60 * 1000).toString());
    }
  }

  async clearTokens() {
    this.accessToken = null;
    await storage.remove('accessToken');
    await storage.remove('refreshToken');
    await storage.remove('loginTimestamp');
  }

  // Generic API methods
  async get<T>(endpoint: string, params?: Record<string, unknown>): Promise<T> {
    const response = await this.client.get(endpoint, { params });
    return response.data;
  }

  async post<T>(endpoint: string, data?: unknown): Promise<T> {
    const response = await this.client.post(endpoint, data);
    return response.data;
  }

  async put<T>(endpoint: string, data?: unknown): Promise<T> {
    const response = await this.client.put(endpoint, data);
    return response.data;
  }

  async patch<T>(endpoint: string, data?: unknown): Promise<T> {
    const response = await this.client.patch(endpoint, data);
    return response.data;
  }

  async delete<T>(endpoint: string): Promise<T> {
    const response = await this.client.delete(endpoint);
    return response.data;
  }

  async isAuthenticated(): Promise<boolean> {
    if (!this.accessToken) {
      // Try to initialize from storage if not already
      await this.initializeAuth();
    }
    console.log('[Auth] isAuthenticated accessToken:', this.accessToken);
    return !!this.accessToken;
  }

  getCurrentUserId(): string | null {
    if (!this.accessToken) return null;
    
    try {
      const decoded = jwtDecode<CustomJwtPayload>(this.accessToken);
      return decoded.userId || decoded.sub || null;
    } catch (error) {
      console.error('Error decoding token:', error);
      return null;
    }
  }

  getUserRole(): string | null {
    if (!this.accessToken) return null;
    
    try {
      const decoded = jwtDecode<CustomJwtPayload>(this.accessToken);
      return decoded.role || null;
    } catch (error) {
      console.error('Error decoding token:', error);
      return null;
    }
  }
}

// Create and export a singleton instance
export const apiClient = new ApiClient();
export default apiClient;
